import type { PsHttpSSE } from '@patsnap-utils/sse';

/**
 * SSE 流式消息类型
 */
export interface RecommendTsSseThinking {
    type: 'thinking';
    content: string;
}

export interface RecommendTsSseResult {
    type: 'result';
    content: {
        patent_info: PatentInfo;
        recommend_ts: RecommendedStandard[];
        message: string;
    };
}

export interface RecommendTsSseDone {
    type: 'done';
}

export type RecommendTsSseMessage = RecommendTsSseThinking | RecommendTsSseResult | RecommendTsSseDone;

/**
 * 推荐技术标准请求参数
 */
export interface RecommendTsRequest {
    /**
     * 任务 ID
     */
    task_id: string;
}

/**
 * 专利信息
 */
export interface PatentInfo {
    /**
     * 专利 ID
     */
    patent_id: string;
    /**
     * 专利号
     */
    patent_number: string;
    /**
     * 申请号
     */
    application_number: string;
    /**
     * 申请日期
     */
    application_date: number;
}

/**
 * 推荐的技术标准
 */
export interface RecommendedStandard {
    /**
     * 标准 ID
     */
    std_id: string;
    /**
     * 标准编号
     */
    number: string;
    /**
     * 版本
     */
    version: string;
    /**
     * 发布版本
     */
    release: string[];
    /**
     * 标题
     */
    title: string;
    /**
     * 项目
     */
    project: string[];
    /**
     * 工作组
     */
    work_group: string;
    /**
     * 相关性评分
     */
    relevance_score: string;
    /**
     * 推荐理由
     */
    reason: string;
    /**
     * 是否已验证
     */
    validated: boolean;
    /**
     * 版本日期
     */
    version_date?: string;
}

/**
 * 推荐技术标准响应
 */
export interface RecommendTsResponse {
    /**
     * 请求状态
     */
    status: boolean;
    /**
     * 错误代码
     */
    error_code: number;
    /**
     * 错误消息
     */
    error_msg?: string;
    /**
     * 响应数据
     */
    data?: {
        /**
         * 专利信息
         */
        patent_info: PatentInfo;
        /**
         * 推荐的技术标准列表
         */
        recommend_ts: RecommendedStandard[];
        /**
         * 消息
         */
        message: string;
    };
}

/**
 * 确认选择的技术标准项
 */
export interface SelectedTsItem {
    /**
     * 标准 ID
     */
    std_id: string;
}

/**
 * 确认选择请求参数
 */
export interface ConfirmTsRequest {
    /**
     * 任务 ID
     */
    task_id: string;
    /**
     * 选中的技术标准列表
     */
    selected_ts: SelectedTsItem[];
}

/**
 * 确认选择响应
 */
export interface ConfirmTsResponse {
    /**
     * 请求状态
     */
    status: boolean;
    /**
     * 错误代码
     */
    error_code: number;
    /**
     * 错误消息
     */
    error_msg?: string;
    /**
     * 响应数据
     */
    data?: {
        /**
         * 任务 ID
         */
        task_id: string;
        /**
         * 选中的技术标准列表
         */
        selected_ts: SelectedTsItem[];
    };
}

export const RecommendTsApi = Symbol('RecommendTsApi');

/**
 * 推荐技术标准 API
 */
export interface RecommendTsApi {
    /**
     * 获取推荐的技术标准
     */
    getRecommendTs(request: RecommendTsRequest): Promise<RecommendTsResponse>;

    /**
     * 获取推荐的技术标准（SSE 流式）
     */
    getRecommendTsStream(request: RecommendTsRequest): Promise<PsHttpSSE<RecommendTsSseMessage>>;

    /**
     * 确认选择的技术标准
     */
    confirmTs(request: ConfirmTsRequest): Promise<ConfirmTsResponse>;
}
